import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { appColors, BackrsIcon, Header } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { SelectMenu } from '@backrs_ui/grow-ui/src/components/SelectMenu/SelectMenu';
import { axiosInstance as axios } from '@backrs_ui/grow-ui/src/core/axios';
import {
  CoachingStyle,
  AppLanguage,
  SourcesOfTruth,
  OpenAIModel,
  COACHING_STYLES,
  SOURCES_OF_TRUTH,
  OPENAI_MODELS,
  OFFICE_HOURS_SESSION_TITLES,
} from '@backrs_ui/grow-ui/src/models/models';
import {
  resetBoxData,
  setIsLoadingResources,
  selectResourcesContent,
  setResourcesContent,
} from '@backrs_ui/grow-ui/src/redux/boxData/boxData';
import {
  resetLubavMessages,
  setOpenAIModel,
  setAppLanguage,
  setCoachingStyle,
  setSourcesOfTruth,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import { initiateSession } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.thunk';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getSessionState,
  createSessionAction,
  typeGuardForOfficeHoursSessionType,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import { RootState } from '@backrs_ui/grow-ui/src/redux/store';
import { selectDoesUserHaveRole, selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';
import { setLubavSession } from '@backrs_ui/grow-ui/src/utils/setLubavSession';

import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Select,
  MenuItem,
  Tooltip,
  SelectChangeEvent,
  SxProps,
  Theme,
  ClickAwayListener,
} from '@mui/material';

import { OfficeHoursContent } from './molecules/OfficeHoursContent';
import { SupercoachUserDropdown } from './molecules/SuperCoachUserDropdown';

const { header } = appStrings.modules;

type OfficeHoursProps = Readonly<{
  superuser: boolean;
  supercoach: boolean;
}>;

export const OfficeHours = ({ superuser, supercoach }: OfficeHoursProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = useAppSelector(selectUserId);
  const dispatch = useAppDispatch();
  const betaMode = useAppSelector(selectDoesUserHaveRole('Lubav Beta'));

  const activeSession = setLubavSession(location.pathname, dispatch);

  const {
    coachingStyle,
    language,
    sourcesOfTruth,
    costs,
    openAIModel: LLM,
  } = useAppSelector((state: RootState) => getSessionState(state, activeSession).coachLubav);

  const resourcesContent = useAppSelector((state) => selectResourcesContent(state, activeSession));
  const { currentResponseId } = useAppSelector(
    (state) => getSessionState(state, activeSession).boxData,
  );

  const { sessionId } = useAppSelector((state) => getSessionState(state, activeSession).coachLubav);

  const handleSettingCoachingStyle = (updatedCoachingStyle: string) => {
    dispatch(
      createSessionAction(setCoachingStyle, activeSession)(updatedCoachingStyle as CoachingStyle),
    );
  };

  const handleSettingAppLanguage = (event: SelectChangeEvent<AppLanguage>) => {
    dispatch(createSessionAction(setAppLanguage, activeSession)(event.target.value as AppLanguage));
  };

  const handleSettingSourcesOfTruth = (updatedSourcesOfTruth: string) => {
    dispatch(
      createSessionAction(
        setSourcesOfTruth,
        activeSession,
      )(updatedSourcesOfTruth as SourcesOfTruth),
    );
  };

  const handleUpdatingOpenAIModel = (updatedOpenAIModel: string) => {
    dispatch(createSessionAction(setOpenAIModel, activeSession)(updatedOpenAIModel as OpenAIModel));
  };

  const [collapsed, setCollapsed] = useState(true);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  useEffect(() => {
    if (!sessionId && userId) {
      const hasValidSessionType = params.id && typeGuardForOfficeHoursSessionType(params.id);

      if (!(supercoach || superuser) && !hasValidSessionType) {
        navigate(APP_ROUTE.NOT_FOUND_404);

        return;
      }

      dispatch(createSessionAction(resetLubavMessages, activeSession)(undefined));
      dispatch(createSessionAction(resetBoxData, activeSession)(undefined));
      dispatch(
        initiateSession({
          userId,
          path: window.location.pathname,
          officeHoursId: params.id,
          axios,
          activeSession,
        }),
      );
    }
  }, [dispatch, userId, sessionId, params.id, navigate, supercoach, superuser, activeSession]);

  const getTitleText = () => {
    if (!activeSession) return null;

    return typeGuardForOfficeHoursSessionType(activeSession)
      ? OFFICE_HOURS_SESSION_TITLES[activeSession]
      : '';
  };

  async function getAdditionalResources(responseId: number | undefined) {
    if (!responseId || !activeSession) return;

    dispatch(createSessionAction(setIsLoadingResources, activeSession)(true));
    const resourcesResponse = await axios.post('/api/office-hours/get-resources', {
      response_id: responseId,
      session_id: sessionId,
      user_id: userId,
      llm: LLM,
      language: language,
      as_superuser: superuser,
    });

    dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));
    dispatch(
      createSessionAction(
        setResourcesContent,
        activeSession,
      )(resourcesResponse.data.output || resourcesContent),
    );
  }

  const middleSection = (
    <Box sx={middleSectionStyle}>
      {supercoach && <SupercoachUserDropdown />}
      {betaMode && (
        <>
          <Box>
            <SelectMenu
              value={coachingStyle}
              onChange={handleSettingCoachingStyle}
              title={header.coachingStyleTitle}
              options={Object.values(COACHING_STYLES).map((value) => ({
                value,
                label: header.coachingStyleMap[value],
              }))}
            />
          </Box>

          <Box>
            <SelectMenu
              value={sourcesOfTruth}
              onChange={handleSettingSourcesOfTruth}
              title={header.sourcesOfTruthTitle}
              options={Object.values(SOURCES_OF_TRUTH).map((value) => ({
                value,
                label: header.sourcesOfTruthMap[value],
              }))}
            />
          </Box>

          <Box>
            <SelectMenu
              value={LLM}
              onChange={handleUpdatingOpenAIModel}
              title={header.modelTitle}
              options={Object.values(OPENAI_MODELS).map((value) => ({
                value,
                label: header.modelMap[value],
              }))}
            />
          </Box>
        </>
      )}
    </Box>
  );

  const rightSection = (
    <>
      <Box sx={{ ...rightSectionContainerStyle, ...(supercoach ? { marginLeft: '5%' } : {}) }}>
        <Typography sx={officeHoursTitleStyle}>{getTitleText()}</Typography>
      </Box>
      <Box sx={controlsContainerStyle}>
        {betaMode && (
          <>
            <Tooltip title="View Token Usage">
              <IconButton sx={languageButtonStyle} onClick={() => setCollapsed((prev) => !prev)}>
                <img src="/token-outline.svg" alt="View Token Usage" style={{ width: '32px' }} />
              </IconButton>
            </Tooltip>
            <Collapse in={!collapsed} orientation="horizontal">
              <Box sx={{ display: 'flex', gap: '24px' }}>
                <Box sx={textAlignRight}>
                  <Typography sx={costStyle}>{header.totalCost}</Typography>
                  <Typography sx={costFont}>${costs.total_cost.toFixed(3)}</Typography>
                </Box>
                <Box sx={textAlignRight}>
                  <Typography sx={costStyle}>{header.inputTokens}</Typography>
                  <Typography sx={costFont}>{costs.prompt_tokens.toLocaleString()}</Typography>
                </Box>
                <Box sx={textAlignRight}>
                  <Typography sx={costStyle}>{header.outputTokens}</Typography>
                  <Typography sx={costFont}>{costs.completion_tokens.toLocaleString()}</Typography>
                </Box>
              </Box>
            </Collapse>
          </>
        )}
        <Box>
          <ClickAwayListener onClickAway={() => setIsTooltipOpen(false)}>
            <Tooltip
              title="Change Language"
              open={isTooltipOpen}
              onClose={() => setIsTooltipOpen(false)}
              onMouseEnter={() => setIsTooltipOpen(true)}
              onMouseLeave={() => setIsTooltipOpen(false)}
              placement="left"
              arrow
              disableFocusListener
              disableHoverListener
              disableTouchListener
            >
              <Select
                value={language}
                onChange={(event) => {
                  handleSettingAppLanguage(event);
                  setIsTooltipOpen(false);
                }}
                displayEmpty
                renderValue={() => (
                  <IconButton sx={languageButtonStyle}>
                    <img
                      src="/language-toggle.svg"
                      alt="Change Language"
                      style={languageIconStyle}
                    />
                  </IconButton>
                )}
                sx={languageSelectStyle}
              >
                <MenuItem value="english">{header.english}</MenuItem>
                <MenuItem value="spanish">{header.spanish}</MenuItem>
                <MenuItem value="french">{header.french}</MenuItem>
              </Select>
            </Tooltip>
          </ClickAwayListener>
        </Box>
        <Tooltip title="Refresh Resources">
          <IconButton
            onClick={() => getAdditionalResources(currentResponseId)}
            sx={{
              ...languageButtonStyle,
              opacity: currentResponseId ? 1 : 0.5,
              pointerEvents: currentResponseId ? 'auto' : 'none',
            }}
          >
            <BackrsIcon name="IconRefresh" alt="refresh_resources" />
          </IconButton>
        </Tooltip>
      </Box>
    </>
  );

  if (!activeSession || !typeGuardForOfficeHoursSessionType(activeSession)) {
    return null;
  }

  return (
    <Box sx={containerStyle}>
      <Header title={header.askLubav} middleSection={middleSection} rightSection={rightSection} />
      <OfficeHoursContent superuser={superuser} supercoach={supercoach} />
    </Box>
  );
};

const containerStyle: SxProps<Theme> = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const middleSectionStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  '@media (max-width: 700px)': {
    flexDirection: 'column',
  },
};

const rightSectionContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'flex-start',
};

const officeHoursTitleStyle: SxProps<Theme> = {
  fontWeight: 'bold',
  color: appColors.color.blue1,
  whiteSpace: 'nowrap',
  fontSize: '24px',
};

const controlsContainerStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const languageButtonStyle: SxProps<Theme> = {
  width: '40px',
  height: '40px',
};

const languageIconStyle: React.CSSProperties = {
  width: '28px',
  height: '28px',
};

const languageSelectStyle: SxProps<Theme> = {
  width: '40px',
  height: '32px',
  backgroundColor: 'transparent',
  border: 'none',
  padding: 0,
  margin: 0,
  '.MuiSelect-icon': {
    display: 'none',
  },
  '.MuiSelect-select': {
    padding: 0,
    border: 'none',
  },
  '&:hover': {
    border: 'none',
    boxShadow: 'none',
    backgroundColor: 'transparent',
  },
  '&:focus': {
    border: `none`,
  },
  '&.Mui-focused': {
    outline: 'none',
    boxShadow: 'none',
    border: `none`,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& fieldset': {
    border: 'none',
  },
};

const costStyle: SxProps<Theme> = {
  fontWeight: 'bold',
  fontSize: '10pt',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const costFont: SxProps<Theme> = {
  fontSize: '10pt',
};

const textAlignRight: SxProps<Theme> = {
  textAlign: 'right',
};
